import React, { useContext, useEffect, useMemo, useState } from "react";
import { Context } from "../../../../Context";
import operationData from "../../../../../Operation/data.json";
import { fetchOrders, updateOrderStatus } from "../../../../utils/foodDeliveryApi";
import FoodDeliveryTabs from "./FoodDeliveryTabs";

const STATUS_TABS = [
  { id: "new", label: "NEW", statuses: ["placed", "payment_initiated", "confirmed", "payment_done", "pending", "created", "order_placed", "paid"] },
  { id: "preparing", label: "PREPARING", statuses: ["accepted", "preparing"] },
  { id: "delivery", label: "ACTIVE", statuses: ["ready", "accepted_by_delivery", "delivery_accepted", "arrived_at_restaurant", "order_on_the_way", "arrived_at_customer", "out_for_delivery"] },
];

const STATUS_CONFIG = {
  cancelled: { bg: "#FEE2E2", text: "#DC2626" },
  rejected: { bg: "#FEE2E2", text: "#DC2626" },
  returned: { bg: "#FEE2E2", text: "#DC2626" },
  delivered: { bg: "#D1FAE5", text: "#059669" },
  order_placed: { bg: "#DBEAFE", text: "#1D4ED8" },
  placed: { bg: "#DBEAFE", text: "#1D4ED8" },
  pending: { bg: "#DBEAFE", text: "#1D4ED8" },
  accepted: { bg: "#DBEAFE", text: "#1D4ED8" },
  preparing: { bg: "#DBEAFE", text: "#1D4ED8" },
  ready: { bg: "#DBEAFE", text: "#1D4ED8" },
  out_for_delivery: { bg: "#DBEAFE", text: "#1D4ED8" },
};

const normalizeListResponse = (response) => {
  if (Array.isArray(response)) return response;
  const candidate = response?.data || response?.items || response?.result || response?.body;
  return Array.isArray(candidate) ? candidate : [];
};

const getOrderStatus = (order) => (order.orderStatus || order.status || "").toString().toLowerCase();

const Orders = () => {
  const { institutionData } = useContext(Context);
  const institution = operationData.foodDeliveryInstitution;
  const activeInstitution = institution || institutionData?.institution || "";

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("new");
  const [prepTimes, setPrepTimes] = useState({});
  const [rejectionModal, setRejectionModal] = useState({ visible: false, orderId: null });
  const [rejectionReason, setRejectionReason] = useState("");

  const loadOrders = async () => {
    if (!activeInstitution) return;
    setLoading(true);
    try {
      const res = await fetchOrders(activeInstitution);
      setOrders(normalizeListResponse(res));
    } catch (err) {
      console.error('Error loading orders:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, [activeInstitution]);

  const filteredOrders = useMemo(() => {
    const tab = STATUS_TABS.find((t) => t.id === activeTab);
    if (!tab) return [];
    return (orders || []).filter((o) => tab.statuses.includes(getOrderStatus(o)));
  }, [orders, activeTab]);

  const sendStatusUpdate = async (orderId, status, extra = {}) => {
    setLoading(true);
    try {
      await updateOrderStatus(activeInstitution, orderId, {
        status,
        ...extra,
      });
      await loadOrders();
    } finally {
      setLoading(false);
    }
  };

  const confirmRejectOrder = async () => {
    if (!rejectionReason.trim()) return alert("Please provide a reason");
    await sendStatusUpdate(rejectionModal.orderId, "rejected", { rejectionReason });
    setRejectionModal({ visible: false, orderId: null });
    setRejectionReason("");
  };

  return (
    <div className="min-h-screen w-full bg-gray-50">
      <FoodDeliveryTabs />
      <div className="px-4 py-6 lg:px-8">
        <div className="mx-auto max-w-5xl space-y-4">
          {/* Header */}
          <div className="bg-white px-6 py-6 rounded-lg shadow-sm border border-gray-200">
            <h1 className="text-2xl font-extrabold text-[#1F2937]">Food Delivery — Orders</h1>
            <p className="mt-1 text-sm text-gray-500">{activeInstitution}</p>
          </div>

          {/* Status Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-1 bg-white rounded-lg shadow-sm border border-gray-200 p-3">
            {STATUS_TABS.map((tab) => {
              const count = (orders || []).filter((order) => tab.statuses.includes(getOrderStatus(order))).length;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors whitespace-nowrap ${isActive ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                >
                  <span>{tab.label}</span>
                  {count > 0 && (
                    <span className={`rounded-full px-2 py-0.5 text-xs ${isActive ? "bg-white text-gray-900" : "bg-gray-900 text-white"}`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {loading && <div className="text-sm text-gray-500 py-4">Loading orders...</div>}

          {/* Orders List */}
          <div className="space-y-3">
            {!loading && filteredOrders.length === 0 && (
              <div className="rounded-lg border border-dashed border-gray-300 px-4 py-8 text-center bg-white">
                <p className="text-sm text-gray-500">No {activeTab} orders found</p>
              </div>
            )}

            {filteredOrders.map((order, index) => {
              const orderId = order.orderId || order.id || index;
              const status = getOrderStatus(order);
              const statusConfig = STATUS_CONFIG[status] || { bg: "#F3F4F6", text: "#6B7280" };
              const items = Array.isArray(order.items) ? order.items : [];
              const displayItems = items.slice(0, 2);
              const remainingCount = items.length - 2;

              return (
                <div key={orderId} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition">
                  {/* Header Row */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-gray-900">#{String(orderId).slice(-6).padStart(6, '0')}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(order.createdAt || order.date || "").toLocaleDateString()} • {new Date(order.createdAt || order.date || "").toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      <div className="mt-1 inline-block px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: statusConfig.text }}>
                        {status.toUpperCase()}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-gray-900">₹{Number(order.totalAmount || order.amount || 0).toFixed(2)}</p>
                      {order.rating && <p className="text-xs text-gray-500 mt-1">⭐ {order.rating}</p>}
                    </div>
                  </div>

                  {/* Customer Info */}
                  <div className="mt-3 flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1 text-gray-700">
                      <span>👤</span>
                      <span>{order.customerName || 'Guest'}</span>
                    </div>
                    <div className="flex items-center gap-1 text-gray-700">
                      <span>📞</span>
                      <span>{order.phone || order.customerPhone || order.contact || 'N/A'}</span>
                    </div>
                  </div>

                  {/* Items Summary */}
                  {items.length > 0 && (
                    <div className="mt-3 space-y-1 text-sm text-gray-600">
                      {displayItems.map((item, idx) => (
                        <p key={idx}>{item.quantity}× {item.itemName || item.name}</p>
                      ))}
                      {remainingCount > 0 && <p className="text-gray-500">+{remainingCount} more</p>}
                    </div>
                  )}

                  {/* Review */}
                  {order.review && (
                    <div className="mt-3 p-2 bg-gray-50 rounded text-sm text-gray-700 border-l-2 border-gray-300">
                      💬 {order.review}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="mt-4 pt-3 border-t border-gray-100 space-y-2">
                    {/* NEW Orders - Accept/Reject */}
                    {status === 'placed' || status === 'payment_done' || status === 'pending' || status === 'order_placed' || status === 'confirmed' ? (
                      <>
                        {/* Preparation Time Selector */}
                        <div className="flex items-center gap-2 mb-3 bg-gray-50 p-3 rounded">
                          <label className="text-xs font-semibold text-gray-700">Prep Time:</label>
                          <button
                            onClick={() => setPrepTimes({ ...prepTimes, [orderId]: Math.max(1, (prepTimes[orderId] || 20) - 1) })}
                            className="px-2 py-1 bg-white border border-gray-300 rounded text-sm hover:bg-gray-100"
                          >
                            −
                          </button>
                          <span className="min-w-12 text-center font-semibold text-gray-900">{prepTimes[orderId] || 20}m</span>
                          <button
                            onClick={() => setPrepTimes({ ...prepTimes, [orderId]: (prepTimes[orderId] || 20) + 1 })}
                            className="px-2 py-1 bg-white border border-gray-300 rounded text-sm hover:bg-gray-100"
                          >
                            +
                          </button>
                        </div>

                        <button
                          onClick={() => sendStatusUpdate(orderId, 'accepted', { preparationTime: prepTimes[orderId] || 20 })}
                          disabled={loading}
                          className="w-full rounded-lg bg-green-600 text-white px-4 py-2 font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        >
                          ✓ ACCEPT ORDER
                        </button>
                        <button
                          onClick={() => setRejectionModal({ visible: true, orderId })}
                          disabled={loading}
                          className="w-full rounded-lg bg-red-600 text-white px-4 py-2 font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        >
                          ✕ REJECT ORDER
                        </button>
                      </>
                    ) : null}

                    {/* PREPARING/ACCEPTED Orders - Mark Ready */}
                    {status === 'accepted' || status === 'preparing' ? (
                      <button
                        onClick={() => sendStatusUpdate(orderId, 'ready')}
                        disabled={loading}
                        className="w-full rounded-lg bg-blue-600 text-white px-4 py-2 font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                      >
                        ✓ MARK READY
                      </button>
                    ) : null}

                    {/* READY/DELIVERY Orders - Mark Delivered & Return */}
                    {status === 'ready' || status === 'out_for_delivery' || status === 'delivery_accepted' || status === 'accepted_by_delivery' ? (
                      <>
                        <button
                          onClick={() => {
                            if (window.confirm('Mark this order as delivered?')) {
                              sendStatusUpdate(orderId, 'delivered');
                            }
                          }}
                          disabled={loading}
                          className="w-full rounded-lg bg-green-600 text-white px-4 py-2 font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        >
                          ✓ MARK DELIVERED
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm('Return (cancel) this order? This will not be counted in revenue.')) {
                              sendStatusUpdate(orderId, 'returned', { cancellationReason: 'return' });
                            }
                          }}
                          disabled={loading}
                          className="w-full rounded-lg bg-red-600 text-white px-4 py-2 font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        >
                          ↶ RETURN ORDER
                        </button>
                      </>
                    ) : null}
                  </div>

                  {/* Footer with Total & Rating */}
                  <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                    <p className="font-semibold text-gray-900">TOTAL: ₹{Number(order.totalAmount || order.amount || 0).toFixed(2)}</p>
                    {order.rating && (
                      <div className="flex items-center gap-1">
                        {[...Array(Math.round(order.rating))].map((_, i) => (
                          <span key={i}>⭐</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {rejectionModal.visible && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
            <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
              <h3 className="text-lg font-bold text-gray-900">Reject Order</h3>
              <p className="mt-1 text-sm text-gray-500">Please provide a reason for rejection.</p>
              <textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} className="mt-4 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" rows={4} placeholder="Reason" />
              <div className="mt-4 flex justify-end gap-3">
                <button onClick={() => { setRejectionModal({ visible: false, orderId: null }); setRejectionReason(''); }} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700">Cancel</button>
                <button onClick={confirmRejectOrder} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white">Yes, Reject</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;
